// src/components/tour-types/forms/UpdateTourType.tsx
import React, { useEffect, useState } from "react";
import Field from "@/components/shared/ui/Field";
import Input from "@/components/shared/ui/Input";
import { FiType, FiFileText } from "react-icons/fi";

export type EditTourTypePayload = {
  name: string;
  description?: string;
};

export type EditTourTypeProps = {
  id: number | string;
  name: string;
  description?: string;
};

export default function UpdateTourType({
  tourType,
  onSubmit,
  onCancel,
}: {
  tourType: EditTourTypeProps;
  onSubmit: (payload: EditTourTypePayload) => void;
  onCancel?: () => void;
}) {
  const [form, setForm] = useState<EditTourTypePayload>({
    name: tourType.name,
    description: tourType.description ?? "",
  });

  // Keep form in sync if parent swaps the tour type while modal is open
  useEffect(() => {
    setForm({
      name: tourType.name,
      description: tourType.description ?? "",
    });
  }, [tourType]);

  const change = <K extends keyof EditTourTypePayload>(
    k: K,
    v: EditTourTypePayload[K]
  ) => setForm((p) => ({ ...p, [k]: v }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSubmit({
      name: form.name.trim(),
      description: form.description?.trim() || "",
    });
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-4">
      {/* Tour type name */}
      <Field label="Tour type name" icon={<FiType />}>
        <Input
          value={form.name}
          onChange={(e) => change("name", e.target.value)}
          placeholder="e.g., Family, Honeymoon, Group"
          required
        />
      </Field>

      {/* Description */}
      <Field label="Description" icon={<FiFileText />}>
        <Input
          value={form.description ?? ""}
          onChange={(e) => change("description", e.target.value)}
          placeholder="Short internal description (optional)"
        />
      </Field>

      {/* Actions */}
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          onClick={() => onCancel?.()}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
        >
          Save Changes
        </button>
      </div>
    </form>
  );
}
